// src/components/DenialManagement.js
import React from 'react';
import './DenialManagement.css';

const DenialManagement = () => {
  return (
    <main className="denial-management-page">
      {/* Hero Section */}
      <section className="denial-hero">
        <div className="container">
          <div className="denial-hero-content">
            <h1 className="denial-hero-title">
              Expert Healthcare <span className="highlight">Denial Management Services</span>
            </h1>
            <p className="denial-hero-description">
              Our professional denial management services, including coding denial management solutions help healthcare 
              providers use claim denials, recover lost revenue, and maximize full compliance with medical billing and 
              coding regulations in the USA.
            </p>
            <div className="denial-hero-stats">
              <div className="hero-stat-badge">
                Cut Claim Denials by 58% a Year! Your Patient Revenue
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="denial-intro">
        <div className="container">
          <div className="denial-intro-content">
            <h2 className="section-title">
              Denials Management Services | <span className="highlight">Medical Claims Denial Management</span>
            </h2>
            <p className="intro-text">
              Reduce Claim Denials, Maximize Reimbursement. Strengthen Your Revenue Cycle. Claim denials are one of the 
              leading causes of lost revenue for healthcare providers. At Provider Care Billing LLC, we offer professional 
              medical claim denial management services designed to reduce denial rates, recover unpaid claims, and improve 
              overall revenue cycle management (RCM) performance.
            </p>
            <p className="intro-text">
              Our denial management experts use a data-driven approach to identify root cause, custom timely appeals, and 
              implement preventive strategies that help practices achieve higher claim and reimbursement rates.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="denial-services">
        <div className="container">
          <div className="services-header">
            <h2 className="section-title-dark">Our Denial Management Services</h2>
            <p className="services-subtitle">
              We review all denied and disputed claims and categorize them by denial reason, payer, provider, and service 
              type to prioritize high-impact opportunities for recovery.
            </p>
          </div>
          
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">🔍</div>
              <h3 className="service-card-title">Root Cause Analysis</h3>
              <p className="service-card-text">
                Our team conducts in-depth denial trend analyses to identify issues such as:
              </p>
              <ul className="service-list">
                <li>Coding errors and mismatches</li>
                <li>Missing or invalid authorization</li>
                <li>Timely filing limits</li>
                <li>Medical necessity issues</li>
                <li>Duplicate claims or billing errors</li>
                <li>Payer-specific rule violations</li>
              </ul>
            </div>

            <div className="service-card">
              <div className="service-icon">📝</div>
              <h3 className="service-card-title">Claims Corrections & Appeals Management</h3>
              <p className="service-card-text">
                We manage the full insurance appeal process, including:
              </p>
              <ul className="service-list">
                <li>Review and correction of claim data</li>
                <li>Timely filing of appeals</li>
                <li>Level I and Level II appeals</li>
                <li>Coordination with payers</li>
                <li>Documentation gathering</li>
                <li>Status tracking and follow-up</li>
              </ul>
            </div>

            <div className="service-card">
              <div className="service-icon">💰</div>
              <h3 className="service-card-title">Underpayment & Contractual Analysis</h3>
              <p className="service-card-text">
                Compare reimbursement to our contracted rates to identify underpayments.
              </p>
              <ul className="service-list">
                <li>Contract rate verification</li>
                <li>Payment reconciliation</li>
                <li>Underpayment identification</li>
                <li>Payer contract compliance</li>
                <li>Reimbursement recovery</li>
              </ul>
            </div>

            <div className="service-card">
              <div className="service-icon">📞</div>
              <h3 className="service-card-title">Payer Follow-Up & Escalation</h3>
              <p className="service-card-text">
                Payor follow-up is escalation to minimize resources and overbills.
              </p>
              <ul className="service-list">
                <li>Dedicated payer follow-up</li>
                <li>Escalation procedures</li>
                <li>Aging claim management</li>
                <li>Payer relationship management</li>
                <li>Timely resolution tracking</li>
              </ul>
            </div>

            <div className="service-card">
              <div className="service-icon">🛡️</div>
              <h3 className="service-card-title">Denial Prevention Strategies</h3>
              <p className="service-card-text">
                Our team conducts in-depth denial trend analysis to identify issues such as:
              </p>
              <ul className="service-list">
                <li>Pre-submission claim scrubbing</li>
                <li>Staff training and education</li>
                <li>Process improvement recommendations</li>
                <li>Automated validation rules</li>
                <li>Real-time eligibility verification</li>
              </ul>
            </div>

            <div className="service-card">
              <div className="service-icon">📊</div>
              <h3 className="service-card-title">Denial Identification & Categorization</h3>
              <p className="service-card-text">
                Identify, resolve, and prevent denials to ensure timely payments to end-revenue lines.
              </p>
              <ul className="service-list">
                <li>Automated denial tracking</li>
                <li>Denial reason categorization</li>
                <li>Priority-based workflow</li>
                <li>Performance reporting</li>
                <li>Recovery rate analytics</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Specialty Expertise Section */}
      <section className="specialty-expertise">
        <div className="container">
          <div className="expertise-content">
            <h2 className="expertise-title">
              Specialty-Specific <span className="highlight">Denials Expertise</span>
            </h2>
            <p className="expertise-text">
              We provide denial management services for all medical specialties, designing dedicated denial specialists 
              and account managers who understand specialty-specific coding, authorization, and payer documentation 
              requirements.
            </p>
            <p className="expertise-text">
              We analyze denial patterns, identify root cause, and implement targeted strategies to prevent recurring 
              issues. By ensuring accurate claim correction and timely appeals, we help improve reimbursement rates, 
              reduce sign AR, and strengthen overall revenue cycle performance for your practice.
            </p>
            <div className="expertise-features">
              <div className="expertise-feature">
                <span className="feature-check">✓</span>
                <span>Specialty-specific coding expertise</span>
              </div>
              <div className="expertise-feature">
                <span className="feature-check">✓</span>
                <span>Dedicated denial specialists</span>
              </div>
              <div className="expertise-feature">
                <span className="feature-check">✓</span>
                <span>Root cause analysis</span>
              </div>
              <div className="expertise-feature">
                <span className="feature-check">✓</span>
                <span>Preventive strategy implementation</span>
              </div>
            </div>
          </div>
        </div>
      </section>
       {/* Reporting & Analytics Section - NEW */}
      <section className="reporting-analytics">
        <div className="container">
          <div className="analytics-header">
            <h2 className="analytics-title">Reporting & Analytics</h2>
            <p className="analytics-text">
              We perform strategic denial analytics. Our healthcare coding denial control professionals develop understanding 
              of complex claims types, helping identify issues and recommend corrective actions. This data-driven approach 
              ensures our clients receive the highest level of service and support.
            </p>
          </div>
          <div className="analytics-features">
            <div className="analytics-item">
              <div className="analytics-check">✓</div>
              <span>Denial rates by payer and category</span>
            </div>
            <div className="analytics-item">
              <div className="analytics-check">✓</div>
              <span>Appeal success and overturn rates</span>
            </div>
            <div className="analytics-item">
              <div className="analytics-check">✓</div>
              <span>Recovered and prevented revenue totals</span>
            </div>
            <div className="analytics-item">
              <div className="analytics-check">✓</div>
              <span>Actionable recommendations for denial reduction</span>
            </div>
          </div>
        </div>
      </section>

       {/* Benefits Section - NEW */}
      <section className="benefits-section">
        <div className="container">
          <div className="benefits-header">
            <h2 className="benefits-title">Benefits of Professional Denial Management</h2>
            <p className="benefits-subtitle">
              A powerful denial management initiative transforms denials into a proactive example to improve revenue. 
              Along with us, you would benefit from:
            </p>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">✓</div>
              <span className="benefit-text">Improved payer compliance</span>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">✓</div>
              <span className="benefit-text">Stronger revenue cycle performance</span>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">✓</div>
              <span className="benefit-text">Higher clean-claim rates</span>
            </div>
          </div>
        </div>
      </section>

        <section className="denial-process">
        <div className="container">
          <div className="process-header">
            <h2 className="process-title">Our Denials Management Process</h2>
            <p className="process-subtitle">
              The denial management services offered by Providers Care Billing LLC are structured on an analytical-driven 
              workflow that is aimed at recovering revenue in the shortest possible time and at eliminating the problematic 
              causes of denials. Our process includes:
            </p>
          </div>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-text">Identify and analyze denied insurance claims</div>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-text">Investigate the reasons for denial</div>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-text">Contact carriers and adjust appeals</div>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-text">Track appeal documents and payment reports</div>
            </div>
            <div className="process-step">
              <div className="step-number">5</div>
              <div className="step-text">Implement denial prevention strategies</div>
            </div>
            <div className="process-step">
              <div className="step-number">6</div>
              <div className="step-text">Monitor trends and provide ongoing reporting</div>
            </div>
          </div>
        </div>
      </section>

       {/* Why Choose Us Section - NEW */}
      <section className="why-choose-denial">
        <div className="container">
          <div className="why-choose-content">
            <h2 className="why-choose-title">
              Why Choose Providers Care Billing LLC For Your <span className="highlight">Denial Management Services</span>
            </h2>
            <p className="why-choose-text">
              Providers Care Billing LLC is your trusted partner for denial management services, offering timely handling 
              of claim denials within 24 to 48 hours. Our team consists of experienced medical billing and denial management 
              specialists who ensure HIPAA-compliant and secure workflows. We have in-depth expertise in navigating 
              Medicare, Medicaid, and commercial payer guidelines, ensuring accurate and efficient claim processing. 
              With transparent communication and regular performance reporting, we keep you informed every step of the way. 
              By leveraging proven revenue cycle management best practices, Providers Care Billing LLC helps maximize your 
              reimbursements and optimize your financial outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="denial-cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Reduce Your Claim Denials?</h2>
            <p className="cta-text">
              Contact us today for a free denial management assessment and see how we can help recover lost revenue.
            </p>
            <button className="cta-button">Schedule a Free Consultation →</button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default DenialManagement;